import { Component, OnInit, Output, EventEmitter, Input, ViewChild, SimpleChanges, OnChanges } from '@angular/core';
import { Platform } from '@ionic/angular';
import { BasePage } from '../../base-page';
import { Upload } from 'src/app/core/data/schema/upload';
import { Utilities } from '../../helper/util';
import { DomSanitizer } from '@angular/platform-browser';
import { Camera, Photo, CameraResultType } from '@capacitor/camera';
import { ContentPreviewComponent } from '../content-preview/content-preview.component';
import { ServiceHelper } from '../../helper/services.helper';
import { AuthenticationService } from '../../modules/auth-core/data/service/authentication.service';

@Component({
  selector: 'app-content-upload',
  templateUrl: './content-upload.component.html',
  styleUrls: ['./content-upload.component.scss'],
})
export class ContentUploadComponent extends BasePage implements OnInit, OnChanges {

  @ViewChild('viewer', { static: true }) contentViewer: ContentPreviewComponent;
  @Output() fileSelected: EventEmitter<Upload> = new EventEmitter();
  @Output() fileRemoved: EventEmitter<Upload> = new EventEmitter();

  @Input() uploadTitle = 'Select File';
  @Input() uploadDetails;
  @Input() promptForDelete = false;
  @Input() placeHolderImg = '';
  @Input() showUploadButtonText = true;
  @Input() files: Upload[] = [];
  @Input() config: IUploadConfig = {
    maxFiles: 1,
    allowChange: true,
    showPreview: true,
    acceptedTypes: 'image/*',
    previewHeight: 150
  };
  @Input() disabled = false;

  filesRemaining = 0;
  id: string;
  lastFile: Upload = new Upload();
  libraryShown = false;

  constructor(
    private platform: Platform,
    private sanitizer: DomSanitizer,
    authService: AuthenticationService
  ) {
    super(authService);
  }

  get isCordova() { return this.platform.is('capacitor'); }

  ngOnInit() {
    this.id = this.getuploadId();
    const validFiles = [];

    // if files aren't an array, make them an array
    if (!Array.isArray(this.files)) {
      this.files = [this.files];
    }

    this.files.forEach(f => { if (f != null) { validFiles.push(f); }; });
    this.files = validFiles;
    this.filesRemaining = (this.config.maxFiles - this.files.length);

  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.config.showPreview === false && this.files.length === 1) {
      this.lastFile = this.files[0];
    }
    if (this.files == null) {
      this.reset();
    } else if (!Array.isArray(this.files) && (this.files as Upload).path == null) {
      this.reset();
    }
  }

  reset() {
    this.filesRemaining = this.config.maxFiles;
    this.files = [];
  }

  async removeFile(file: Upload) {
    if (this.promptForDelete && !(await ServiceHelper.confirmSomething('Delete Image?', 'Are you sure you want to delete this image?', 'No', 'Yes') === true)) {
      return;
    }
    this.fileRemoved.emit(file);
    this.filesRemaining++;

    // Remove the file if needed
    const idOf = this.files.indexOf(file);
    if (idOf > -1) {
      this.files.splice(idOf, 1);
    }

    this.lastFile = new Upload();
  }

  onBrowse() {
    if (this.isCordova) {
      this.showLibrary();
    } else {
      this.triggerBrowse();
    }
  }

  triggerBrowse() {
    this.libraryShown = false;
    document.getElementById(this.id).click();
  }

  showLibrary() {
    this.libraryShown = !this.libraryShown;
  }

  async selectFile(selFile: any) {
    if (!this.canAddMoreMedia) {
      return;
    }

    const maxSizeInMb = this.config.maxFileSizeInMb || 100;

    // Verify we can add pictures (give warnings)
    if (selFile != null && selFile.target.files.length > 0) {
      for (const file of selFile.target.files) {
        const blob = file as Blob;
        const maxSizeInBytes = maxSizeInMb * 1024 * 1024;
        const blobSizeInMb = parseFloat(((blob.size / 1024) / 1024).toFixed(2));
        if (blob.size > maxSizeInBytes) {
          ServiceHelper.displayAlert('File too big', `Cannot upload file. Size too large: ${blobSizeInMb}MB. Max ${maxSizeInMb}MB allowed.`);
        } else {
          let path = '';
          if (this.isImage(file.name)) {
            path = await Utilities.resizeImage(blob, 1280, 720) as string;
          } else {
            path = URL.createObjectURL(file);
          }
          this.prepareForUpload(path, file.name, file.type);
        }
      }
      // reset input value so we can reselect the same file
      selFile.target.value = null;
    }
  }

  async takePicture() {
    if (!this.canAddMoreMedia) {
      return;
    }

    // Continue on
    this.libraryShown = false;

    try {
      // Turn the image into base64 and add to the pending
      const image = await Camera.getPhoto({
        quality: 75,
        correctOrientation: true,
        height: 720,
        width: 1280,
        allowEditing: false, // still kind of buggy
        resultType: CameraResultType.Uri
      }) as Photo;

      // convert the base 64 into a data url
      // const imgUrl = URL.createObjectURL();
      // Log.debug(image);
      this.prepareForUpload(image.webPath, null, 'image/jpeg');
    } catch (error) {
      ServiceHelper.displayAlert(
        'Camera Error',
        (error === 'cordova_not_available' ? 'This option is only available on device with camera' : error)
      );
    }
  }

  private isImage(fileName: string) {
    const images: string[] = ['jpg', 'bmp', 'png', 'svg', 'jpeg'];
    const split = fileName.split('.');
    const extension = split[split.length - 1].toLowerCase();
    return (images.indexOf(extension) > -1);
  }

  private prepareForUpload(path: string, name: string, type: string) {
    // If it's capacitor, then we need to make a proper URL based on the one created above
    if (this.isCordova) {
      const webView = (window as any).Ionic.WebView;
      path = webView.convertFileSrc(path);
    }

    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(path);
    this.lastFile = new Upload(path);
    this.lastFile.safeUrl = safeUrl;
    this.lastFile.mimeType = type;
    this.lastFile.thumbPath = safeUrl;
    this.lastFile.filename = name;
    this.lastFile.testType();
    this.fileLoaded(this.lastFile);
  }

  private canAddMoreMedia() {
    if (this.filesRemaining === 0 || this.config.allowChange) {
      ServiceHelper.displayAlert('Too Many Files', `You can only select up to ${this.config.maxFiles} files.`);
      return false;
    }
    return true;
  }

  private fileLoaded(file: Upload) {
    // emit that we loaded it
    this.fileSelected.emit(file);
    if (this.files.length + 1 > this.config.maxFiles) {
      this.files.shift(); // remove the first item to allow new item to be added
    }
    this.files.push(file);
    this.files = Object.assign([], this.files);
    this.filesRemaining--;
    if (this.config.showPreview) {
      this.contentViewer.imageAdded();
    }
  }

  private getuploadId() {
    let text = '';
    const possible = 'abcdefghijklmnopqrstuvwxyz1234679';
    for (let i = 0; i < 3; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

}

export interface IUploadConfig {
  showPreview?: boolean;
  maxFiles?: number;
  allowVideoCapture?: boolean;
  allowChange?: boolean;
  fields?: string[];
  acceptedTypes?: string;
  showThumbnails?: boolean;
  allowRemove?: boolean;
  previewHeight?: number;
  thumbSize?: number;
  mode?: string;
  label?: string;
  maxFileSizeInMb?: number;
}
